import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  FilterLiveSearch,
  SelectInput,
  DateInput,
  BooleanInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  ShowButton,
  EditButton,
  useRedirect,
} from 'react-admin';
import { Chip, Stack, IconButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';
import { PageWrapper } from '../../components/PageWrapper';
import { MaintenanceShowDialog } from '../../components/dialogs/MaintenanceShowDialog';

const serviceTypes = [
  { id: 'OIL_CHANGE', name: 'Oil Change' },
  { id: 'INSPECTION', name: 'Inspection' },
  { id: 'REPAIR', name: 'Repair' },
  { id: 'TIRE_SERVICE', name: 'Tire Service' },
  { id: 'BRAKE_SERVICE', name: 'Brake Service' },
  { id: 'ENGINE_SERVICE', name: 'Engine Service' },
  { id: 'TRANSMISSION_SERVICE', name: 'Transmission Service' },
  { id: 'ELECTRICAL', name: 'Electrical' },
  { id: 'BODYWORK', name: 'Bodywork' },
  { id: 'PREVENTIVE', name: 'Preventive Maintenance' },
  { id: 'EMERGENCY', name: 'Emergency Repair' },
  { id: 'OTHER', name: 'Other' }
];

const fileTypeIcons: Record<string, React.ReactElement> = {
  receipt: <ReceiptIcon fontSize="small" />,
  photo: <ImageIcon fontSize="small" />,
  report: <AssignmentIcon fontSize="small" />,
  invoice: <DescriptionIcon fontSize="small" />,
  default: <AttachFileIcon fontSize="small" />,
};

const FileCountField = () => {
  const record = useRecordContext();
  const files = record?.files || [];
  
  if (files.length === 0) return null;
  
  // Group files by type
  const filesByType = files.reduce((acc: any, file: any) => {
    const type = file.fileType || 'default';
    if (!acc[type]) acc[type] = 0;
    acc[type]++;
    return acc;
  }, {});
  
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {Object.entries(filesByType).map(([type, count]) => (
        <Chip
          key={type}
          icon={fileTypeIcons[type] || fileTypeIcons.default}
          label={count}
          size="small"
          variant="outlined"
          color="primary"
        />
      ))}
    </Stack>
  );
};

const ServiceTypeField = () => {
  const record = useRecordContext();
  if (!record?.serviceType) return null;
  
  const getColor = (type: string) => {
    switch (type) {
      case 'PREVENTIVE': return 'success';
      case 'EMERGENCY': return 'error';
      case 'REPAIR': return 'warning';
      case 'INSPECTION': return 'info';
      default: return 'default';
    }
  };
  
  return (
    <Chip 
      label={record.serviceType.replace(/_/g, ' ')} 
      color={getColor(record.serviceType)}
      size="small"
    />
  );
};

const CostField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  
  if (!value) return <span>-</span>;
  
  return (
    <span>${Number(value).toLocaleString()}</span>
  );
};

const MaintenanceEmptyState = createEmptyStateComponent('maintenance');

const MaintenanceListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const useMaintenanceFilters = () => {
  return React.useMemo(
    () => [
      <FilterLiveSearch key="description" source="description" placeholder="Search maintenance..." />,
      <SelectInput 
        key="serviceType"
        source="serviceType" 
        choices={serviceTypes} 
        label="Service Type"
        alwaysOn
      />,
      <DateInput key="serviceDate" source="serviceDate" label="Service Date" />,
      <BooleanInput key="scheduledService" source="scheduledService" label="Scheduled Only" />,
      <BooleanInput key="underWarranty" source="underWarranty" label="Under Warranty" />,
    ],
    []
  );
};

const ViewButton = ({ onClick }: { onClick: (id: number) => void }) => {
  const record = useRecordContext();
  if (!record) return null;
  
  return (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        onClick(record.id);
      }}
      size="small"
      color="primary"
    >
      <VisibilityIcon fontSize="small" />
    </IconButton>
  );
};

export const EquipmentMaintenanceList = () => {
  const filters = useMaintenanceFilters();
  const redirect = useRedirect();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  
  const handleOpenDialog = (id: number) => {
    setSelectedRecordId(id);
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRecordId(null);
  };
  
  const handleEdit = () => {
    if (selectedRecordId) {
      redirect('edit', 'equipment-maintenance', selectedRecordId);
      handleCloseDialog();
    }
  };
  
  return (
    <PageWrapper title="Equipment Maintenance">
      <List
        perPage={25}
        sort={{ field: 'serviceDate', order: 'DESC' }}
        filters={filters}
        actions={<MaintenanceListActions />}
        empty={<MaintenanceEmptyState />}
      >
      <Datagrid bulkActionButtons={false}>
        <TextField source="id" sortable={false} />
        <ReferenceField source="equipmentId" reference="equipment">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="serviceDate" />
        <ServiceTypeField />
        <TextField source="serviceProvider" />
        <NumberField source="serviceMileage" />
        <NumberField source="serviceEngineHours" />
        <CostField source="totalCost" />
        <BooleanField source="scheduledService" />
        <BooleanField source="underWarranty" />
        <FileCountField />
        <ViewButton onClick={handleOpenDialog} />
        <EditButton />
      </Datagrid>
    </List>
    
    <MaintenanceShowDialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      recordId={selectedRecordId}
      onEdit={handleEdit}
    />
    </PageWrapper>
  );
};